import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  ReadyCashStamentCardDataInterface,
  ReadyCashStatementsFilterInterface,
  ReadyCashStatementsListStatementsDataInterface,
  ReadyCashStatementsTableDataInterface,
  ReadyCashViewStatementInterface,
  ReadyCashStatementBreakdownDetailsDataInterface,
} from '@/types/readyCashInterface';
import { isDateBefore } from '@/helpers/dateParser';
import { PaginationFintechInterface } from '@/types/cashAdvanceInterface';
import { GetStatementsMonthly } from '@/services/ReadyCash/GetStatementsMonthly';
import { GetStatement } from '@/services/ReadyCash/GetStatement';
import { GetTransactionListData } from '@/services/ReadyCash/GetTransactionListData';
import { GetStatementBreakdown } from '@/services/ReadyCash/GetStatementBreakdown';

export const useReadyCashViewStatementsStore = defineStore(
  'readyCashViewStatementsStore',
  () => {
    const LD_ViewStatements = ref<boolean>(false);
    const openViewStatement = ref<boolean>(false);
    const viewStatement = ref<ReadyCashViewStatementInterface>({
      month: null,
      year: null,
    });
    const viewStatementLoader = ref<boolean>(false);
    const tableStatementLoader = ref<boolean>(false);
    const isDownloadModalOpen = ref<boolean>(false);
    const isStatementsViewDetailsOpen = ref<boolean>(false);
    const isStatementsFiltersOpen = ref<boolean>(false);
    const showSelectedFieldsLength = ref<boolean>(false);
    const tempSelectedFieldsLength = ref<number>(0);
    const isServerErrorWithRefVisible = ref<boolean>(false);
    const isServerErrorNoRefVisible = ref<boolean>(false);
    const serverErrorReferenceKey = ref<string>('');
    const searchStatementList = ref<string | null>(null);
    const selectedStatementDate = ref<string>('');
    const currentActivity = ref<string>('');
    const periodCutoff = ref<Array<{ label: string; disabled: boolean }>>([
      {
        label: '',
        disabled: false,
      },
    ]);
    const activeTab = ref<Array<{ id: string; periodcutoff: number }>>([
      {
        id: '',
        periodcutoff: 0,
      },
    ]);
    const isStatementBreakdownLoaded = ref<boolean>(false);
    const isBreakdownLoading = ref<boolean>(false);
    const statementID = ref<string | null>(null);
    const downloadSnackbar = ref(null);
    const viewStatementTableData = ref({
      data: [] as ReadyCashStatementsTableDataInterface[],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      } as PaginationFintechInterface,
    });
    const selectedFields = ref<ReadyCashStatementsFilterInterface>({
      datePicker: [{ fromDate: '', toDate: '' }],
      paymentStatus: [],
      employeeStatus: [],
    });

    const selectedFieldsToFilter = ref<ReadyCashStatementsFilterInterface>({
      datePicker: [{ fromDate: '', toDate: '' }],
      paymentStatus: [],
      employeeStatus: [],
    });

    const tempSelectedFields = ref<ReadyCashStatementsFilterInterface>({
      datePicker: [{ fromDate: '', toDate: '' }],
      paymentStatus: [],
      employeeStatus: [],
    });

    const paginationFintech = ref<PaginationFintechInterface>({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    });

    const transactionListTablePagination = ref<PaginationFintechInterface>({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    });
    const listStatementsData = ref(
      [] as ReadyCashStatementsListStatementsDataInterface[]
    );

    const statementCardData = ref<ReadyCashStamentCardDataInterface>(
      {} as ReadyCashStamentCardDataInterface
    );

    const statementBreakdownData =
      ref<ReadyCashStatementBreakdownDetailsDataInterface>({
        statementId: '',
        dueDate: '',
        statementBalance: 0,
        breakdown: {
          principalAmount: 0,
          convenienceFee: 0,
          balance: 0,
        },
        totalAdjustments: 0,
        totalStatementBalance: 0,
      });

    const handleErrorResponse = (error: {
      response?: { data?: { referenceKey?: string }; status?: number };
    }) => {
      console.error(error);

      const referenceKey = error?.response?.data?.referenceKey;
      const errorStatus = error?.response?.status;

      if (errorStatus >= 400) {
        if (referenceKey) {
          serverErrorReferenceKey.value = referenceKey;
          isServerErrorWithRefVisible.value = true;
        } else {
          isServerErrorNoRefVisible.value = true;
        }
      }
    };

    const getStatementsMonthly = async () => {
      try {
        const { year, month } = viewStatement.value;
        const { currentPage } = paginationFintech.value;

        const response = await GetStatementsMonthly({
          year: year,
          month: month,
          PageNumber: currentPage,
        });
        if (typeof response !== 'object') {
          return;
        }
        const newItems = response.data.data;
        listStatementsData.value = [...listStatementsData.value, ...newItems];

        Object.assign(paginationFintech.value, response.data.pagination);
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        viewStatementLoader.value = false;
      }
    };

    const getStatement = async (id: string) => {
      try {
        const response = await GetStatement(id);
        if (typeof response !== 'object') {
          return;
        }
        statementCardData.value = response.data;
      } catch (error) {
        handleErrorResponse(error);
      }
    };

    const getTransactionListTableData = async (id: string) => {
      tableStatementLoader.value = true;

      try {
        const { currentPage, itemsPerPage } =
          transactionListTablePagination.value;
        const { datePicker, paymentStatus, employeeStatus } = selectedFieldsToFilter.value;

        const search = searchStatementList.value;
        const datefrom = datePicker[0].fromDate;
        const dateto = datePicker[0].toDate;
        const paymentstatus = paymentStatus;
        const employeestatus = employeeStatus;

        const response = await GetTransactionListData({
          id: id,
          PageNumber: currentPage,
          RowsPerPage: itemsPerPage,
          search: search,
          datefrom: datefrom,
          dateto: dateto,
          paymentstatus: paymentstatus,
          employeestatus: employeestatus,
        });
        if (typeof response !== 'object') {
          return;
        }
        viewStatementTableData.value.data = response.data.data;
        transactionListTablePagination.value = response.data.pagination;
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        tableStatementLoader.value = false;
      }
    };

    const getSearchStatement = async () => {
      tableStatementLoader.value = true;
      transactionListTablePagination.value.currentPage = 1;

      if (
        searchStatementList.value !== null &&
        searchStatementList.value !== ''
      ) {
        currentActivity.value = 'search';
      }

      try {
        await getTransactionListTableData(statementID.value);
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        tableStatementLoader.value = false;
      }
    };

    const getFilteredStatement = async () => {
      currentActivity.value = 'filter';
      tableStatementLoader.value = true;
      transactionListTablePagination.value.currentPage = 1;
      selectedFieldsToFilter.value = selectedFields.value;
      try {
        await getTransactionListTableData(statementID.value);
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        tableStatementLoader.value = false;
      }
    };

    const resetSelectedFields = async () => {
      if (currentActivity.value === 'filter') {
        if (
          searchStatementList.value !== null &&
          searchStatementList.value !== ''
        ) {
          currentActivity.value = 'search';
        } else currentActivity.value = '';
      }
      tableStatementLoader.value = true;
      tempSelectedFields.value = null;
      tempSelectedFieldsLength.value = 0;
      transactionListTablePagination.value.currentPage = 1;
      try {
        await getTransactionListTableData(statementID.value);
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        tableStatementLoader.value = false;
      }
    };

    const getStatementBreakdown = async (id: string) => {
      if (isStatementBreakdownLoaded.value) {
        return;
      }

      try {
        const response = await GetStatementBreakdown(id);
        if (typeof response !== 'object') {
          return;
        }
        statementBreakdownData.value = response.data;
        isStatementBreakdownLoaded.value = true;
      } catch (error) {
        handleErrorResponse(error);
      }
    };

    const hasPayrollProcessed = computed(() => {
      return statementCardData.value.status === 'complete';
    });

    const isTransactionProcessed = computed(() => {
      return (
        statementCardData.value.PaidAmount ===
        statementCardData.value.StatementBalance
      );
    });

    const hasNonEmptyValue = (item: string) =>
      Object.values(item).some((date) => date !== '');

    const isNonEmptyArray = (value: string[]) =>
      Array.isArray(value) && value.some(hasNonEmptyValue);

    const isNonEmptyString = (value: string) =>
      typeof value === 'string' && value.length > 0;

    const selectedFieldsLength = computed(() => {
      return Object.keys(selectedFields.value).filter((property) => {
        const value = selectedFields.value[property];
        return (
          isNonEmptyArray(value as string[]) ||
          isNonEmptyString(value as string)
        );
      }).length;
    });

    const isValidToDate = computed(() => {
      const datePicker = selectedFields.value?.datePicker[0];
      if (datePicker?.fromDate && datePicker?.toDate) {
        return isDateBefore(datePicker.toDate, datePicker.fromDate);
      }
      return false;
    });

    const changePage = async (paginate: PaginationFintechInterface) => {
      paginationFintech.value.currentPage = paginate.currentPage;
      paginationFintech.value.itemsPerPage = paginate.itemsPerPage;
      await getStatementsMonthly();
    };

    const changeTransactionListTablePage = async (
      paginate: PaginationFintechInterface
    ) => {
      transactionListTablePagination.value.currentPage = paginate.currentPage;
      transactionListTablePagination.value.itemsPerPage = paginate.itemsPerPage;
      await getTransactionListTableData(statementID.value);
    };

    const showStatementsListLoading = () => {
      viewStatementLoader.value = true;
    };

    const hideStatementsListLoading = () => {
      viewStatementLoader.value = false;
    };

    const showDownloadSnackbarSuccess = () => {
      downloadSnackbar.value.showSuccess({
        text: 'All good! Wait for the download to start.',
        showIcon: true,
      });
    };

    const showDownloadSnackbarError = () => {
      downloadSnackbar.value.showDanger({
        text: 'Error occurred. Please try again.',
        showIcon: true,
      });
    };

    return {
      LD_ViewStatements,
      openViewStatement,
      viewStatement,
      viewStatementLoader,
      isDownloadModalOpen,
      isStatementsViewDetailsOpen,
      isStatementsFiltersOpen,
      showSelectedFieldsLength,
      tempSelectedFieldsLength,
      selectedFields,
      selectedFieldsToFilter,
      tempSelectedFields,
      resetSelectedFields,
      selectedFieldsLength,
      isValidToDate,
      hasPayrollProcessed,
      isTransactionProcessed,
      viewStatementTableData,
      tableStatementLoader,
      changePage,
      paginationFintech,
      showStatementsListLoading,
      hideStatementsListLoading,
      listStatementsData,
      getStatementsMonthly,
      statementCardData,
      getStatement,
      isServerErrorNoRefVisible,
      isServerErrorWithRefVisible,
      serverErrorReferenceKey,
      transactionListTablePagination,
      getTransactionListTableData,
      changeTransactionListTablePage,
      periodCutoff,
      getStatementBreakdown,
      statementBreakdownData,
      isBreakdownLoading,
      statementID,
      selectedStatementDate,
      downloadSnackbar,
      showDownloadSnackbarSuccess,
      showDownloadSnackbarError,
      activeTab,
      searchStatementList,
      getSearchStatement,
      getFilteredStatement,
      currentActivity,
    };
  }
);
